/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Bounds, DiagramLinkData, ItemInfo, Zoom } from '#PRIVATE_BaseDiagram/diagram.types';

export const ZOOM_INCREMENT = 0.05;
const ZOOM_TO_FIT_PADDING = 20;

export const zoom = (
  nextZoom: number,
  zoom: number,
  minZoom: number,
  maxZoom: number,
  onZoomChange?: (detail: { zoomValue: number }) => void
) => {
  const newZoom = nextZoom > maxZoom || nextZoom < minZoom ? zoom : nextZoom;
  const isZoomChanged = newZoom !== zoom;
  isZoomChanged &&
    onZoomChange &&
    onZoomChange({
      zoomValue: newZoom
    });
};

/**
 * Returns the zoom properties for the diagram.
 */
export function getZoomProps(props: Zoom, width: number, height: number, contentBounds: Bounds) {
  const zoomTofitZoom = getZoomToFitZoom(width, height, contentBounds);
  const maxZoom = props.maxZoom || 1;
  const minZoom = props.minZoom || Math.min(maxZoom, zoomTofitZoom);
  const zoom = props.zoomValue ? props.zoomValue : zoomTofitZoom;
  return {
    zooming: props.zooming ? props.zooming : 'off',
    // default value of 0 indicates zoom-to-fit level
    minZoom: minZoom,
    maxZoom: maxZoom,
    // zoom value of 0 will default to zoom to fit
    zoomValue: constrainZoom(zoom, minZoom, maxZoom),
    onZoom: props.onZoom
  };
}

/**
 * Returns the zoom to fit zoom level
 */
export function getZoomToFitZoom(width: number, height: number, contentBounds: Bounds) {
  const zoomX = (width - 2 * ZOOM_TO_FIT_PADDING) / contentBounds.width;
  const zoomY = (height - 2 * ZOOM_TO_FIT_PADDING) / contentBounds.height;
  return Math.min(zoomX, zoomY);
}

/**
 * Constrains the zoom level
 */
function constrainZoom(zoom: number, minZoom: number, maxZoom: number) {
  let newZoom = Math.max(minZoom, zoom);
  newZoom = Math.min(maxZoom, newZoom);
  return newZoom;
}

/**
 * Returns the panX, panY, and zoom when zooming and centering an item
 */
export function zoomAndCenterItemFromInfo<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  info: ItemInfo<K1, K2, D2>,
  linkIdToDataMap: Map<K2, D2>,
  bounds: Map<K1, Bounds>,
  width: number,
  height: number,
  maxZoom: number
) {
  const type = info.type;
  let totalBounds;
  if (type === 'node') {
    totalBounds = bounds.get(info.id as K1)!;
  } else {
    const link = linkIdToDataMap.get(info.id as K2)!;
    const startNodeBounds = bounds.get(link.startNode)!;
    const endNodeBounds = bounds.get(link.endNode)!;
    const minX = Math.min(startNodeBounds.x, endNodeBounds.x);
    const minY = Math.min(startNodeBounds.y, endNodeBounds.y);
    const maxX = Math.max(
      startNodeBounds.x + startNodeBounds.width,
      endNodeBounds.x + startNodeBounds.width
    );
    const maxY = Math.max(
      startNodeBounds.y + startNodeBounds.height,
      endNodeBounds.y + endNodeBounds.height
    );
    totalBounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return getPanZoomToCenterNode(totalBounds, maxZoom, width, height);
}

/**
 * Returns the pan and zoom values in order
 * to center a node or link
 */
function getPanZoomToCenterNode(bounds: Bounds, maxZoom: number, width: number, height: number) {
  const zoomX = (width - 2 * ZOOM_TO_FIT_PADDING) / bounds.width;
  const zoomY = (height - 2 * ZOOM_TO_FIT_PADDING) / bounds.height;
  const zoom = Math.min(zoomX, zoomY, maxZoom);
  const cX = bounds.width / 2 + bounds.x;
  const cY = bounds.height / 2 + bounds.y;
  return { centerX: cX, centerY: cY, zoom: zoom };
}
